import {
  APPROVAL_STATUS,
  AMOUNT,
  COMMUNITY_ID,
  DOCUMENT_MAPPINGS,
  CURRENCY,
  BUDGET_MONTH,
  BUDGET_START_MONTH,
  BUDGET_YEAR,
  BUDGET_END_MONTH,
  HEADER,
  LINE_ITEMS,
  DIST_ZONE,
  DIST_BLOCK,
  DIST_PROPERTY,
  APPROVED_UNIT,
  APPROVED_RATE_PER_UNIT,
  UNIT,
  RATE_PER_UNIT,
  APP_AMOUNT,
  GL_ACCOUNT_NUMBER,
  GL_ACCOUNT_DESCRIPTION,
  DRCR,
  TRANSACTION_TYPE,
  BILLING_MONTH_YEAR,
  POSTING_MONTH,
  POSTING_YEAR,
  SUB_LEDGER,
  COMBINE,
  CLEARING,
} from "./consts";
import { useGetPostedDocById, useGetPropertiesByCommunity, useGetPropertyBlockByCommunityId, useGetPropertyZoneByCommunityId, useGetTrialBalanceDocReportExport } from "@/services";
import { CheckIcon, X } from "lucide-react";
import { drcrOptions, formatAmount, MONTHS, transactionTypeOptions } from "@/lib/utils";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { ColumnType } from "./types";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/Toast";

const useDocumentLineItemDialog = ({ selectedDoc }: { selectedDoc: any }) => {
  const [isEntryView, setIsEntryView] = useState<boolean>(true);
  const [headerFields, setHeaderField] = useState<any>(null);
  const [lineItemFields, setLineItemFields] = useState<any>(null);
  const [openColumnDrawer, setOpenColumnDrawer] = useState<boolean>(false);
  const columnType = useRef<ColumnType>("header");
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const communityId = communityData?.communityId;
  const queryClient = useQueryClient();

  const { data: reportData, isLoading: isReportLoading, isError: isReportError, refetch: refetchReportData } =
    useGetPostedDocById({
      id: selectedDoc?.postDocHeaderId,
      communityId: selectedDoc?.communityId,
      entryView: isEntryView,
      queryParams: {
        enabled: !!(selectedDoc?.postDocHeaderId && selectedDoc?.communityId),
      },
    });

    const {
      data: reportExportData,
      refetch: refetchReportExportData,
      isRefetching: isReportExportLoading,
    } = useGetTrialBalanceDocReportExport({
      postDocHeaderId: selectedDoc?.postDocHeaderId as string,
      communityId: communityId as string,
      queryParams: {
        enabled: false,
      },
    });
  
    useEffect(() => {
      if (!reportExportData) return;
  
      try {
        const blob = new Blob([reportExportData.data], {
          type: reportExportData.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
  
        const contentDisposition = reportExportData.headers["content-disposition"];
        let downloadFileName = `${communityId}-trial-balance-document-report.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) downloadFileName = match[1];
        }
  
        link.setAttribute("download", downloadFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "There is an issue while downloading the file"
        );
      } finally {
        queryClient.removeQueries({
          queryKey: ["trial-balance-doc-report-export", selectedDoc?.postDocHeaderId],
        });
      }
    }, [reportExportData]);

  const onDownloadExcel = () => {
    refetchReportExportData();
  }

  useEffect(() => {
    if (selectedDoc?.postDocHeaderId) {
      refetchReportData();
    }
  }, [isEntryView])

  const lineItems = useMemo(() => {
    if (isEntryView) {
      return reportData?.data?.lineItems?.filter(
        (data: any) => data?.lineItemType?.toLowerCase() !== "offset"
      );
    }
    return reportData?.data?.lineItems;
  }, [reportData, isEntryView]);

  const onColumnClick = (type: any) => {
    columnType.current = type;
    setOpenColumnDrawer(true);
  };

  const { data: zoneList, isLoading: isLoadingZoneList } = useGetPropertyZoneByCommunityId({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: selectedDoc?.documentType == "VI",
    },
  });

  const { data: propertyBlock, isLoading: isBlockLoading } =
    useGetPropertyBlockByCommunityId({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: selectedDoc?.documentType == "VI",
      }
    });

  const { data: propertyList, isLoading: isPropertyLoading } =
    useGetPropertiesByCommunity({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: selectedDoc?.documentType == "VI",
      }
    });

  useEffect(() => {
    if (reportData) {
      const rData = reportData?.data || {};
      let docMap: any = (DOCUMENT_MAPPINGS as any)[rData?.documentType?.toLowerCase()] ? 
        isEntryView ? (DOCUMENT_MAPPINGS as any)[rData?.documentType?.toLowerCase()]["entry"] : (DOCUMENT_MAPPINGS as any)[rData?.documentType?.toLowerCase()]["gl"] :
        isEntryView ? (DOCUMENT_MAPPINGS as any)["other"]["entry"] : (DOCUMENT_MAPPINGS as any)["other"]["gl"];

      const headerData = rData?.[HEADER];
      const headers = docMap[HEADER]?.map((data: any) => {
        let value = data?.key == COMMUNITY_ID ? selectedDoc?.communityId : headerData?.[data?.key];

        if (data.key?.toLowerCase()?.includes("date")) {
          value = headerData?.[data?.key] ? moment(headerData?.[data?.key]).format("MM/DD/YYYY") : "";
        } else if (data.key?.toLowerCase()?.includes("amount")) {
          value = formatAmount(headerData?.[data?.key] || "0");
        } else if (data.key == TRANSACTION_TYPE) {
          value = transactionTypeOptions?.find((val: any) => val.value == headerData?.[data?.key])?.label || "";
        } else if (data?.key == BILLING_MONTH_YEAR) {
          value = `${String(headerData?.[POSTING_MONTH])?.padStart(2, "0")}/${headerData?.[POSTING_YEAR]}`;
        }

        return {...data, value}
      })
      setHeaderField(headers);
      setLineItemFields(docMap[LINE_ITEMS]);
    }
  }, [isEntryView, reportData])

  const isHeader = columnType.current == HEADER;

  const columns = useMemo(
    () => {
      return lineItemFields?.filter((data: any) => data.checked)?.map((data: any) => {
        let accessorFn = null;
        if (data.key == GL_ACCOUNT_NUMBER) {
          accessorFn = (row: any) => `${row?.[GL_ACCOUNT_NUMBER]} - ${row?.[GL_ACCOUNT_DESCRIPTION]}`
        } else if (data?.key == BUDGET_MONTH) {
          accessorFn = (row: any) => `${MONTHS[row?.[BUDGET_START_MONTH] - 1]} ${row?.[BUDGET_YEAR]} - ${MONTHS[row?.[BUDGET_END_MONTH] - 1]} ${row?.[BUDGET_YEAR]}`
        } else if (data.key === APPROVED_UNIT) {
          accessorFn = (row: any) => row?.[UNIT];
        } else if (data.key === APPROVED_RATE_PER_UNIT) {
          accessorFn = (row: any) => formatAmount(row?.[RATE_PER_UNIT] || "0");
        } else if (data.key === APP_AMOUNT) {
          accessorFn = (row: any) => formatAmount(row?.[AMOUNT] || "0");
        } else if (data?.key?.includes("date")) {
          accessorFn = (row: any) => row?.[data?.key] ? moment(row?.[data?.key]).format("MM/DD/YYYY") : ""
        } else if (data.key?.toLowerCase()?.includes("amount") || data.key?.toLowerCase()?.includes("rate") ) {
          accessorFn = (row: any) => formatAmount(row?.[data.key] || "0");
        } else if (data.key === CURRENCY) {
          accessorFn = () => "USD"
        } else if (data.key === DIST_ZONE) {
          accessorFn = (row: any) => zoneList?.data?.find((val: any) => val?.zoneId == row?.[data?.key])?.zoneName;
        } else if (data.key === DIST_BLOCK) {
          accessorFn = (row: any) => propertyBlock?.data?.find((val: any) => val?.blockId == row?.[data?.key])?.blockName;
        } else if (data.key === DIST_PROPERTY) {
          accessorFn = (row: any) => propertyList?.data?.find((val: any) => val?.propertyId == row?.[data?.key])?.name;
        } else if (data.key == DRCR) {
          accessorFn = (row: any) => drcrOptions?.find((val: any) => val.value == row?.[data?.key])?.label || ""; 
        }

        let obj = null;
        if (data?.key == APPROVAL_STATUS) {
          obj = {
            accessorKey: data.key,
            header: data.label,
            cell: ({ row }: any) => {
              if (row?.original?.approvalStatus?.toLowerCase() === "approved") {
                return (
                  <CheckIcon className="h-4 w-4 stroke-green-600 font-bold" />
                );
              }
              return <X className="h-4 w-4 stroke-red-600 font-bold" />;
            },
          }
        } else if (data?.key == SUB_LEDGER) {
          obj = {
            accessorKey: data.key,
            header: data.label,
            cell: ({ row }: any) => {
              if (row?.original?.[SUB_LEDGER]) {
                return (
                  <CheckIcon className="h-4 w-4 stroke-green-600 font-bold" />
                );
              } 
              return <X className="h-4 w-4 stroke-red-600 font-bold" />;
            },
          }
        } else if (data?.key == COMBINE) {
          obj = {
            accessorKey: data.key,
            header: data.label,
            cell: ({ row }: any) => {
              if (row?.original?.[COMBINE]) {
                return (
                  <CheckIcon className="h-4 w-4 stroke-green-600 font-bold" />
                );
              } 
              return <X className="h-4 w-4 stroke-red-600 font-bold" />;
            },
          }
        } else if (data?.key == CLEARING) {
          obj = {
            accessorKey: data.key,
            header: data.label,
            cell: ({ row }: any) => {
              if (row?.original?.[CLEARING]) {
                return (
                  <CheckIcon className="h-4 w-4 stroke-green-600 font-bold" />
                );
              } 
              return <X className="h-4 w-4 stroke-red-600 font-bold" />;
            },
          }
        } else {
          obj = {
            accessorKey: data.key,
            header: data.label,
            accessorFn: accessorFn,
          };
        }

        return obj
      })
    },
    [lineItemFields, propertyBlock, propertyList, zoneList]
  );


  const onSaveColumns = (data: any) => {
    if (isHeader) {
      setHeaderField(data);
    } else {
      setLineItemFields(data);
    }
    setOpenColumnDrawer(false);
  };

  return {
    isReportLoading: isReportLoading || isLoadingZoneList || isBlockLoading || isPropertyLoading,
    isReportError,
    reportData: reportData?.data,
    columns,
    isEntryView,
    setIsEntryView,
    lineItems,
    onColumnClick,
    headerFields,
    lineItemFields,
    openColumnDrawer,
    setOpenColumnDrawer,
    isHeader,
    onSaveColumns,
    onDownloadExcel,
    isReportExportLoading
  };
};

export default useDocumentLineItemDialog;
